import React from 'react'
import useStore from '@/stores/useStore'
import { formatCurrency } from '@/lib/utils'
import {
  projectStructuredCashBalance,
  selectCashBalanceDesign,
} from '@/lib/planDesign'

export function buildCashBalanceSchedule({ annualCredit, interestRate, years, startYear }) {
  const rows = []
  let openingBalance = 0
  for (let index = 0; index < years; index += 1) {
    const interestCredit = openingBalance * interestRate
    const endingBalance = openingBalance + interestCredit + annualCredit
    rows.push({
      year: startYear + index,
      openingBalance,
      annualCredit,
      interestCredit,
      endingBalance,
    })
    openingBalance = endingBalance
  }
  return rows
}

export default function CashBalanceScheduleTable({ planKey = 'planA' }) {
  const plan = useStore((state) => state[planKey] || state.planA)
  const calculatedEmployees = useStore((state) => state.analysis[planKey]?.calculatedEmployees || state.analysis.planA?.calculatedEmployees)
  const design = selectCashBalanceDesign(plan)

  const model = React.useMemo(() => {
    if (design?.mode !== 'structured' || design.enabled !== true) return null
    const employees = calculatedEmployees || []
    const projected = projectStructuredCashBalance(design)
    if (projected.groups.length === 0) return null
    const annualCredit = employees.reduce((sum, row) => sum + (Number(row.cashBalanceCredit) || 0), 0)
    const years = 10
    const interestRate = Math.max(0, Number(projected.interestCreditRate) || 0) / 100
    const rows = buildCashBalanceSchedule({ annualCredit, interestRate, years, startYear: Number(plan.planYear) || new Date().getFullYear() })
    return { annualCredit, years, interestRate, rows }
  }, [calculatedEmployees, design, plan?.planYear])

  if (!model) return null

  return (
    <details className="rounded-md border border-border" data-testid="cash-balance-schedule-table">
      <summary className="cursor-pointer px-4 py-3 text-sm font-semibold">
        Cash balance funding schedule
        <span className="ml-2 font-normal text-muted-foreground">
          {model.years}-year fixed-credit illustration
        </span>
      </summary>
      <div className="space-y-3 border-t border-border px-4 py-3">
        <p className="text-xs text-muted-foreground">
          Repeats the current employee list&rsquo;s adopted year-one benefit credits at each year end and applies the fixed interest-credit rate to the opening balance. Compensation, employee data, and plan terms stay unchanged. This is not required funding or an actuarial valuation.
        </p>
        <div className="max-h-96 overflow-auto rounded-md border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card text-muted-foreground">
              <tr className="border-b">
                <th className="p-2 text-left font-medium">Plan year</th>
                <th className="p-2 text-right font-medium">Opening balance</th>
                <th className="p-2 text-right font-medium">Benefit credits</th>
                <th className="p-2 text-right font-medium">Interest credit</th>
                <th className="p-2 text-right font-medium">Projected balance</th>
              </tr>
            </thead>
            <tbody>
              {model.rows.map((row) => (
                <tr key={row.year} className="border-b last:border-0">
                  <td className="p-2 font-medium">{row.year}</td>
                  <td className="p-2 text-right tabular-nums text-muted-foreground">{formatCurrency(row.openingBalance)}</td>
                  <td className="p-2 text-right tabular-nums">{formatCurrency(row.annualCredit)}</td>
                  <td className="p-2 text-right tabular-nums text-muted-foreground">{formatCurrency(row.interestCredit)}</td>
                  <td className="p-2 text-right tabular-nums font-medium">{formatCurrency(row.endingBalance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </details>
  )
}
